import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReduxActions from 'reduxActions';
import { Components, Utils, translate } from 'versafleet-core';
import TaskHelper from 'utils/taskHelper';
import withRouterHooks from 'utils/routerHelper';
import { isUserReadOnly } from 'utils/accessHelper';
import Detail from './detail';
import DeleteDialog from './archive/deleteDialog';
import CancelSuccessMessage from './cancel/cancelSuccessMessage';

const {
  FullScreenLoading, Helper, Dialog,
} = Utils;

class TaskViewForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      disableButton: {
        print: false,
      },
      showCancelModal: false,
    };
  }

  UNSAFE_componentWillMount() {
    FullScreenLoading.FullScreenLoadingManager.open(this.props.strings.Task.page.loadingItem);
    this.props.dispatch(ReduxActions.versadriveSetting.fetchVersadriveSetting());
    this.props.dispatch(ReduxActions.task.fetch(Utils.Router.getParams().id))
      .then((res) => {
        if ((res?.task?.custom_field_group || {}).id) {
          this.props.dispatch(ReduxActions.customFieldGroup.fetch(res.task.custom_field_group.id));
        }
      })
      .then(() => {
        FullScreenLoading.FullScreenLoadingManager.close();
      });
  }

  _archive = async () => {
    FullScreenLoading.FullScreenLoadingManager.open(
      this.props.strings.Task.page.archivingItem,
    );
    await this.props.dispatch(ReduxActions.task.archive(this.props.data.id));
    FullScreenLoading.FullScreenLoadingManager.close();
  }

  _unarchive = () => {
    this.props.dispatch(ReduxActions.task.unarchive(this.props.data.id))
      .then(() => {
        if (Helper.isNumber(Utils.Router.getParams().id)) {
          this.props.navigate('/archives/tasks');
        }
      });
  }

  _delete() {
    Dialog.DialogManager.open((
      <DeleteDialog
        dataIds={[this.props.data.id]}
        shouldPopRoute
      />
    ));
  }

  _print() {
    TaskHelper.print(this.props.data.id);
    this._disableButton('print');
  }

  _cancel() {
    this.setState({ showCancelModal: true });
  }

  _routeToJob() {
    if (!Helper.isNullOrUndefined(this.props.data.job)) {
      // Copy path per layer until it hits either tasks or jobs
      let path = '';
      const currentPaths = this.props.location.pathname.split('/').filter(v => v !== '');
      for (let i = 0; i < currentPaths.length; i += 1) {
        if (currentPaths[i] === 'tasks' || currentPaths[i] === 'jobs') {
          break;
        }
        path += `/${currentPaths[i]}`;
      }

      path += `/jobs/${this.props.data.job.id}`;
      this.props.navigate(path);
    }
  }

  _isEditable() {
    const task = this.props.data || {};
    const job = task.job || {};
    let isEditable = !isUserReadOnly();

    if (this.props.profile && this.props.profile.myjobs_setting) {
      // Check task state first
      if (['successful', 'completed'].indexOf(task.state) > -1) {
        isEditable = isEditable && this.props.profile.myjobs_setting.can_edit_after_completion;
      } else if (['submitted', 'in_progress'].indexOf(job.state) > -1) {
        isEditable = isEditable && this.props.profile.myjobs_setting.can_edit_after_submission;
      }
    }

    return isEditable;
  }

  _disableButton(buttonName) {
    this.setState(prevState => ({
      disableButton: { ...prevState.disableButton, [buttonName]: true },
    }), async () => {
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.setState(prevState => ({
        disableButton: { ...prevState.disableButton, [buttonName]: false },
      }));
    });
  }

  async _confirmCancel() {
    try {
      await this.props.dispatch(ReduxActions.task.cancel(this.props.data.id));
      Utils.SuccessMessage.SuccessMessageManager.flash(
        <CancelSuccessMessage count={1} />,
      );
      this.props.navigate('/submissions');
    } catch (error) {
      Utils.SuccessMessage.SuccessMessageManager.flash(
        <div className="vf-task__cancel-dialog__error-message">
          {Utils.TranslationHelper.replaceTag(this.props.strings.Task.cancelDialog.errorMessage, {
            count: 1,
          })}
        </div>,
      );
    }

    this.setState({ showCancelModal: false });
  }

  _renderMenu() {
    if (!Helper.isNullOrUndefined(this.props.data)) {
      return (
        <Components.Form.Title>
          <Components.Form.Title.Item
            id="vf-task__view-form__title__job-return-button"
            onClick={() => this._routeToJob()}
          >
            {(Helper.isNullOrUndefined(this.props.data.job)
              ? this.props.strings.Task.form.constants.noCustomer
              : this.props.data.job.customer.name)}
          </Components.Form.Title.Item>
          <Components.Form.Title.Item
            isHighlighted
          >
            {(Helper.isNullOrUndefined(this.props.data.billing_account)
              ? this.props.strings.Task.form.constants.noAccount
              : this.props.data.billing_account.name)}
          </Components.Form.Title.Item>
        </Components.Form.Title>
      );
    }

    return false;
  }

  _renderRightPanel() {
    const task = this.props.data || {};
    const job = task.job || {};

    if (!Helper.isNullOrUndefined(task)) {
      return (
        <Components.Form.RightPanel>
          {
            this._isEditable() && !task.archived
              ? (
                <Components.Button
                  iconClassName="versa-edit"
                  id="vf-task__view-form__right-panel__edit-button"
                  onClick={() => this.props.navigate(`${Utils.Router.getPath()}/edit`)}
                >
                  {this.props.strings.Task.form.buttons.edit}
                </Components.Button>
              ) : false
          }
          <Components.Button
            disabled={this.state.disableButton.print}
            iconClassName="versa-print"
            id="vf-task__view-form__right-panel__print-button"
            onClick={() => this._print()}
          >
            {this.props.strings.Task.form.buttons.print}
          </Components.Button>
          {
            (this._isEditable()
              && ['draft', 'submitted', 'rejected', 'cancelled'].indexOf(job.state) > -1)
              && !task.archived ? (
                <Components.Archive
                  archive={this._archive}
                  archivePath={() => this.props.navigate('/archives/tasks')}
                  delete={async () => {
                    await this.props.dispatch(ReduxActions.task.delete(task.id));
                  }}
                  ids={[task.id]}
                  modelName={this.props.strings.Task.constants.task}
                  updateContent={() => {
                    this.props.dispatch(ReduxActions.task.fetchList());
                    this.props.navigate('/submissions');
                  }}
                >
                  <Components.Button
                    iconClassName="versa-archive"
                    id="vf-task__view-form__right-panel__archive-button"
                  >
                    {this.props.strings.Task.form.buttons.archive}
                  </Components.Button>
                </Components.Archive>
              ) : false
          }
          {
            (this._isEditable()
              && ['draft', 'submitted', 'rejected', 'cancelled'].indexOf(job.state) > -1)
              && task.archived ? (
                <Components.Unarchive
                  id="vf-task__view-form__right-panel__unarchive-button"
                  modelName={this.props.strings.Task.constants.task}
                  onClick={this._unarchive}
                  withDefaultText
                />
              ) : false
          }
          {
            !task.archived && (task.state === 'successful' || task.state === 'completed') && this.props.profile.show_epod
              && !Helper.isNullOrUndefined(task.epod_url)
              ? (
                <Components.Button
                  iconClassName="versa-epod"
                  iconText=""
                  id="vf-task__view-form__right-panel__epod-button"
                  onClick={() => window.open(task.epod_url, '_blank')}
                >
                  {this.props.strings.Task.form.buttons.viewEPOD}
                </Components.Button>
              ) : false
          }
          {
            task.archived && !isUserReadOnly()
              ? (
                <Components.Button
                  iconClassName="versa-delete"
                  id="vf-task__view-form__right-panel__delete-button"
                  onClick={() => this._delete()}
                >
                  {this.props.strings.Task.form.buttons.delete}
                </Components.Button>
              ) : false
          }
          {
            (this._isEditable() && !task.archived
              && ['successful', 'cancelled', 'completed'].indexOf(task.state) === -1
              && ['draft', 'submitted', 'rejected', 'cancelled'].indexOf(job.state) > -1)
              ? (
                <Components.Button
                  iconClassName="versa-danger"
                  id="vf-task__view-form__right-panel__cancel-button"
                  onClick={() => this._cancel()}
                >
                  {this.props.strings.Task.form.buttons.cancel}
                </Components.Button>
              ) : false
          }
        </Components.Form.RightPanel>
      );
    }

    return false;
  }

  _renderCancelModal() {
    const strings = this.props.strings.Task.cancelDialog;

    return (
      <Components.Modal.Warning
        headerText={strings.header}
        leftButtonOnClick={() => this._confirmCancel()}
        leftButtonText={strings.confirmButton}
        rightButtonOnClick={() => this.setState({ showCancelModal: false })}
        rightButtonText={strings.cancelButton}
        visible={this.state.showCancelModal}
      >
        {strings.line1}
      </Components.Modal.Warning>
    );
  }

  render() {
    if (Helper.isNullOrUndefined(this.props.data)) {
      return false;
    }

    return (
      <Components.Form.CoreLayout
        onClose={() => (this.props.location.pathname.includes('archives')
          ? this.props.navigate('..', { relative: 'path' })
          : this.props.navigate('../..', { relative: 'path' }))}
        rightPanel={this._renderRightPanel()}
        title={this._renderMenu()}
      >
        <Detail
          customFieldGroups={this.props.customFieldGroups}
          data={this.props.data}
          id="vf-task__view-form__left-panel"
        />
        {this._renderCancelModal()}
      </Components.Form.CoreLayout>
    );
  }
}

TaskViewForm.displayName = 'Task View Form';

TaskViewForm.propTypes = {
  customFieldGroups: PropTypes.shape({}),
  data: PropTypes.shape({
    job: PropTypes.shape(),
    id: PropTypes.number,
    billing_account: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  navigate: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    myjobs_setting: PropTypes.shape({
      can_edit_after_completion: PropTypes.bool,
      can_edit_after_submission: PropTypes.bool,
    }),
    show_epod: PropTypes.bool,
  }),
  strings: PropTypes.shape({
    Task: PropTypes.shape({
      cancelDialog: PropTypes.shape({
        errorMessage: PropTypes.string,
        header: PropTypes.string,
        confirmButton: PropTypes.string,
        cancelButton: PropTypes.string,
        line1: PropTypes.string,
      }),
      constants: PropTypes.shape({
        task: PropTypes.string,
      }),
      form: PropTypes.shape({
        buttons: PropTypes.shape({
          edit: PropTypes.string,
          print: PropTypes.string,
          archive: PropTypes.string,
          unarchive: PropTypes.string,
          viewEPOD: PropTypes.string,
          delete: PropTypes.string,
          cancel: PropTypes.string,
        }),
        constants: PropTypes.shape({
          noAccount: PropTypes.string,
          noCustomer: PropTypes.string,
        }),
      }),
      page: PropTypes.shape({
        loadingItem: PropTypes.string,
        archivingItem: PropTypes.string,
      }),
    }),
  }).isRequired,
};

TaskViewForm.defaultProps = {
  customFieldGroups: {},
  data: null,
  profile: null,
};

function mapStateToProps(state, ownProps) {
  const { task, customFieldGroup, profile } = state;
  return {
    profile: profile.profile,
    data: task.tasks[ownProps.params.id],
    customFieldGroups: customFieldGroup.customFieldGroups,
  };
}

export default withRouterHooks(connect(mapStateToProps)(translate('')(TaskViewForm)));
